import { motion } from 'framer-motion';

export default function PrivacyPolicy() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-4xl mx-auto px-4 py-12"
    >
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Politique de confidentialité</h1>
      <p className="text-sm text-gray-500 mb-10">Dernière mise à jour : mai 2026</p>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-10 rounded-r">
        <p className="text-blue-900 font-semibold">
          OPTISECUR s'engage à protéger vos données personnelles conformément au Règlement
          Général sur la Protection des Données (RGPD — Règlement UE 2016/679) et à la
          loi Informatique et Libertés modifiée.
        </p>
      </div>

      <div className="space-y-10">

        {/* 1. Responsable du traitement */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Responsable du traitement</h2>
          <p className="text-gray-600 leading-relaxed">
            Le responsable du traitement de vos données personnelles est :<br /><br />
            <strong>EBELMANN SAS</strong><br />
            SIRET : 94436634300019<br />
            Email : <a href="mailto:support@optisecur.com" className="text-blue-600 hover:underline">support@optisecur.com</a><br />
            Contact général : <a href="mailto:support@optisecur.com" className="text-blue-600 hover:underline">support@optisecur.com</a>
          </p>
        </section>

        {/* 2. Données collectées */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Données collectées</h2>

          <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-4">2.1 Données de compte</h3>
          <p className="text-gray-600 leading-relaxed mb-2">
            Lors de la création d'un compte ou d'une invitation par un conseiller :
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-1">
            <li>Adresse email</li>
            <li>Prénom et nom (optionnels)</li>
            <li>Mot de passe (stocké sous forme hachée, jamais en clair)</li>
          </ul>

          <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-6">2.2 Données du diagnostic de sécurité</h3>
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4 rounded-r">
            <p className="text-green-900 font-semibold">
              Nous ne collectons PAS votre adresse complète ni aucune donnée permettant
              d'identifier précisément votre logement.
            </p>
          </div>
          <p className="text-gray-600 leading-relaxed mb-2">Pour le diagnostic, nous collectons :</p>
          <ul className="list-disc list-inside text-gray-600 space-y-1">
            <li>Commune et code postal (nécessaires pour les statistiques locales de sécurité)</li>
            <li>Type de logement (maison / appartement)</li>
            <li>Caractéristiques générales (étages, jardin, garage, etc.)</li>
            <li>Équipements de sécurité existants</li>
            <li>Profil d'occupation (sans données nominatives)</li>
          </ul>
          <p className="text-gray-600 leading-relaxed mt-3">
            Ces informations sont utilisées pour calculer votre score de sécurité et récupérer
            les statistiques de cambriolages locales via l'API publique <strong>data.gouv.fr</strong>.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-6">2.3 Données de navigation</h3>
          <ul className="list-disc list-inside text-gray-600 space-y-1">
            <li>Données de session (token d'authentification, durée de session)</li>
            <li>Logs techniques nécessaires à la sécurité et au débogage (adresse IP, date/heure)</li>
          </ul>
          <p className="text-gray-600 leading-relaxed mt-3">
            Aucune donnée de navigation n'est transmise à des régies publicitaires ou à des
            courtiers en données.
          </p>
        </section>

        {/* 3. Bases légales */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Bases légales des traitements</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-gray-600 border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left p-3 border border-gray-200 font-semibold text-gray-800">Finalité</th>
                  <th className="text-left p-3 border border-gray-200 font-semibold text-gray-800">Base légale</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-3 border border-gray-200">Gestion du compte utilisateur</td>
                  <td className="p-3 border border-gray-200">Exécution du contrat (Art. 6.1.b RGPD)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-3 border border-gray-200">Réalisation du diagnostic de sécurité</td>
                  <td className="p-3 border border-gray-200">Exécution du contrat (Art. 6.1.b RGPD)</td>
                </tr>
                <tr>
                  <td className="p-3 border border-gray-200">Génération du rapport premium et recommandations</td>
                  <td className="p-3 border border-gray-200">Exécution du contrat (Art. 6.1.b RGPD)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-3 border border-gray-200">Envoi d'emails d'invitation (par un conseiller)</td>
                  <td className="p-3 border border-gray-200">Intérêt légitime (Art. 6.1.f RGPD)</td>
                </tr>
                <tr>
                  <td className="p-3 border border-gray-200">Logs de sécurité technique</td>
                  <td className="p-3 border border-gray-200">Intérêt légitime (Art. 6.1.f RGPD)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-3 border border-gray-200">Amélioration du service (données anonymisées)</td>
                  <td className="p-3 border border-gray-200">Intérêt légitime (Art. 6.1.f RGPD)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 4. Destinataires */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Destinataires des données</h2>
          <p className="text-gray-600 leading-relaxed mb-3">
            Vos données sont accessibles aux seules personnes habilitées au sein d'EBELMANN SAS.
            Elles peuvent également être transmises aux sous-traitants techniques suivants, dans
            le strict cadre de la fourniture du service :
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-2">
            <li>
              <strong>Supabase Inc.</strong> (hébergement base de données et authentification) —
              serveurs UE. Contrat de sous-traitance conforme au RGPD.
            </li>
            <li>
              <strong>Netlify, Inc.</strong> (hébergement de l'application) —
              transfert encadré par les clauses contractuelles types de la Commission européenne.
            </li>
            <li>
              <strong>Stripe, Inc.</strong> (traitement des paiements, le cas échéant) —
              transfert encadré par les clauses contractuelles types.
            </li>
          </ul>
          <p className="text-gray-600 leading-relaxed mt-3">
            Vos données ne sont <strong>jamais vendues</strong> ni cédées à des tiers à des fins
            commerciales ou publicitaires.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Dans le cadre du service conseiller/client, un <strong>conseiller habilité</strong> de
            votre société peut accéder aux résultats de votre diagnostic. Vous en êtes informé
            lors de l'invitation.
          </p>
        </section>

        {/* 5. Transferts hors UE */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Transferts hors Union européenne</h2>
          <p className="text-gray-600 leading-relaxed">
            Certains sous-traitants (Netlify, Stripe) sont établis aux États-Unis. Les transferts
            de données vers ces entités sont encadrés par des <strong>clauses contractuelles
            types (CCT)</strong> adoptées par la Commission européenne, conformément à l'article 46
            du RGPD, garantissant un niveau de protection adéquat.
          </p>
        </section>

        {/* 6. Durée de conservation */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Durée de conservation</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-gray-600 border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left p-3 border border-gray-200 font-semibold text-gray-800">Type de données</th>
                  <th className="text-left p-3 border border-gray-200 font-semibold text-gray-800">Durée de conservation</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-3 border border-gray-200">Compte utilisateur actif</td>
                  <td className="p-3 border border-gray-200">Durée de la relation contractuelle</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-3 border border-gray-200">Données de diagnostic</td>
                  <td className="p-3 border border-gray-200">3 ans après le dernier diagnostic</td>
                </tr>
                <tr>
                  <td className="p-3 border border-gray-200">Compte supprimé ou inactif</td>
                  <td className="p-3 border border-gray-200">12 mois (puis suppression ou anonymisation)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-3 border border-gray-200">Logs techniques</td>
                  <td className="p-3 border border-gray-200">12 mois glissants</td>
                </tr>
                <tr>
                  <td className="p-3 border border-gray-200">Données de paiement</td>
                  <td className="p-3 border border-gray-200">Conservées par Stripe conformément à la réglementation financière (5 ans)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 7. Vos droits */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Vos droits</h2>
          <p className="text-gray-600 leading-relaxed mb-3">
            Conformément au RGPD, vous disposez des droits suivants sur vos données personnelles :
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-2">
            <li><strong>Droit d'accès</strong> — obtenir une copie de vos données (Art. 15)</li>
            <li><strong>Droit de rectification</strong> — corriger des données inexactes (Art. 16)</li>
            <li><strong>Droit à l'effacement</strong> — demander la suppression de vos données (Art. 17)</li>
            <li><strong>Droit à la limitation</strong> — restreindre temporairement un traitement (Art. 18)</li>
            <li><strong>Droit à la portabilité</strong> — recevoir vos données dans un format structuré (Art. 20)</li>
            <li><strong>Droit d'opposition</strong> — vous opposer à un traitement fondé sur l'intérêt légitime (Art. 21)</li>
            <li><strong>Droit de retrait du consentement</strong> — si un traitement repose sur votre consentement (Art. 7)</li>
          </ul>
          <p className="text-gray-600 leading-relaxed mt-4">
            Pour exercer ces droits, contactez-nous à :<br />
            <a href="mailto:support@optisecur.com" className="text-blue-600 hover:underline font-medium">
              support@optisecur.com
            </a>
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Nous nous engageons à répondre à toute demande dans un délai d'<strong>un mois</strong>
            (délai pouvant être prolongé de deux mois en cas de demande complexe).
          </p>
        </section>

        {/* 8. Réclamation CNIL */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Réclamation auprès de la CNIL</h2>
          <p className="text-gray-600 leading-relaxed">
            Si vous estimez que le traitement de vos données personnelles n'est pas conforme à la
            réglementation, vous avez le droit d'introduire une réclamation auprès de la{' '}
            <strong>Commission Nationale de l'Informatique et des Libertés (CNIL)</strong> :
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-1 mt-3">
            <li>Site web : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.cnil.fr</a></li>
            <li>Adresse : 3 Place de Fontenoy – TSA 80715 – 75334 Paris Cedex 07</li>
            <li>Téléphone : 01 53 73 22 22</li>
          </ul>
        </section>

        {/* 9. Sécurité */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Sécurité des données</h2>
          <p className="text-gray-600 leading-relaxed">
            EBELMANN SAS met en œuvre des mesures techniques et organisationnelles appropriées
            pour protéger vos données contre tout accès, modification, divulgation ou destruction
            non autorisés, notamment :
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-1 mt-3">
            <li>Chiffrement des communications (HTTPS / TLS)</li>
            <li>Mots de passe hachés (bcrypt via Supabase Auth)</li>
            <li>Accès aux données restreint par des politiques de sécurité au niveau ligne (RLS)</li>
            <li>Authentification par jeton d'accès à durée limitée</li>
            <li>Sauvegardes régulières des données</li>
          </ul>
        </section>

        {/* 10. Cookies */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Cookies et traceurs</h2>
          <p className="text-gray-600 leading-relaxed">
            L'application OPTISECUR utilise uniquement des <strong>cookies techniques
            strictement nécessaires</strong> au fonctionnement du service (gestion de la
            session d'authentification). Ces cookies ne nécessitent pas de consentement
            préalable conformément à la délibération CNIL n° 2013-378 du 5 décembre 2013.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            <strong>Aucun cookie publicitaire, analytique tiers ou de profilage</strong> n'est
            déposé sur votre terminal.
          </p>
        </section>

        {/* 11. Modifications */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Modifications de la présente politique</h2>
          <p className="text-gray-600 leading-relaxed">
            EBELMANN SAS se réserve le droit de modifier la présente politique de confidentialité
            pour refléter l'évolution du service ou de la réglementation applicable. La date de
            dernière mise à jour est indiquée en haut de ce document. En cas de modification
            substantielle, les utilisateurs inscrits seront informés par email.
          </p>
        </section>

      </div>
    </motion.div>
  );
}
